<script setup lang="ts">
    import { useFontSelector } from '@/composables/useFontSelector';
    import IconChevronDown from '@/components/icons/IconChevronDown.vue';
    import { useElementPosition } from '@/composables/useElementPosition';
    import { ref, useTemplateRef, watch } from 'vue';
    import { onClickOutside } from '@vueuse/core';

    const selectedFont = defineModel<string>();
    const isListVisible = ref(false);

    const { fonts, selectedFontObj, selectFont } = useFontSelector(selectedFont);

    const templateSelectorRef = useTemplateRef('templateSelector');
    const fontContainerRef = useTemplateRef('fontContainer');
    const { style } = useElementPosition(templateSelectorRef);

    onClickOutside(templateSelectorRef, () => (isListVisible.value = false));

    watch(selectedFont, () => {
        isListVisible.value = false;
    });

    watch(fontContainerRef, () => {
        if (!fontContainerRef.value || !selectedFont.value) {
            return;
        }
        const selectedElement = document.getElementById(selectedFont.value);
        selectedElement?.scrollIntoView();
    });
</script>

<template>
    <div
        ref="templateSelector"
        class="relative h-8 w-32 border border-border-primary"
    >
        <button
            class="relative h-full w-full appearance-none p-1"
            @click="isListVisible = !isListVisible"
        >
            <span class="sr-only">{{ $t('select_police') }} {{ selectedFontObj?.fontFace.family }}</span>
            <img
                class="h-6 max-w-28"
                loading="lazy"
                :src="selectedFontObj?.preview"
                :alt="selectedFontObj?.fontFace.family"
            />
            <IconChevronDown :class="['absolute right-2 top-2 transition-transform duration-300', isListVisible && 'rotate-180']" />
        </button>
        <transition
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            class="duration-300"
        >
            <Teleport
                v-if="isListVisible"
                to="body"
            >
                <div
                    v-if="isListVisible"
                    ref="fontContainer"
                    :style
                    class="absolute top-14 z-10 max-h-56 w-72 overflow-y-scroll bg-bg-secondary p-4 shadow-dropdown"
                >
                    <button
                        v-for="font in fonts"
                        :id="font.fontFace.family"
                        :key="font.fontFace.family"
                        :class="[
                            font.fontFace.family === selectedFontObj?.fontFace.family && 'bg-hover-quaternary',
                            'mb-4 appearance-none hover:bg-hover-quaternary',
                        ]"
                        @click="selectFont(font)"
                    >
                        <span class="sr-only">{{ $t('police') }} {{ font.fontFace.family }}</span>
                        <img
                            class="w-64"
                            loading="lazy"
                            :src="font.preview"
                            :alt="font.fontFace.family"
                        />
                    </button>
                </div>
            </Teleport>
        </transition>
    </div>
</template>
